import React from 'react';
import { Link } from 'react-router-dom';
import { SPORTS } from '../utils/constants';
import './HomePage.css';

const SPORT_ICONS = {
  'Basketball': '🏀',
  'Football': '🏈',
  'Soccer': '⚽',
  'Baseball/Softball': '⚾',
  'Tennis': '🎾',
  'Volleyball': '🏐',
  'Wrestling': '🤼',
  'Golf': '⛳',
};

const PARENT_STEPS = [
  {
    number: '01',
    title: 'Find a Trainer',
    desc: 'Browse trainers in your area. Filter by sport, city, and rating to find the right match for your child.',
  },
  {
    number: '02',
    title: 'Create a Free Account',
    desc: 'Sign up as a parent in under a minute — it\'s completely free to join.',
  },
  {
    number: '03',
    title: 'Request a Session',
    desc: 'Send a session request with your preferred date, time, and location. Pay GamePlannr’s $4 booking fee to confirm once the trainer accepts.',
  },
  {
    number: '04',
    title: 'Train & Grow',
    desc: 'Meet up in person and let your child learn from a relatable, community trainer. Pay the trainer directly at their rate.',
  },
];

const MENTOR_STEPS = [
  {
    number: '01',
    title: 'Create Your Profile',
    desc: 'Sign up as a trainer, set your sport, hourly rate, and location. Upload a photo and write a short bio.',
  },
  {
    number: '02',
    title: 'Accept Requests',
    desc: 'Get notified when a parent requests a session. Accept, decline, or suggest a different time. You\'re in control.',
  },
  {
    number: '03',
    title: 'Get Paid',
    desc: 'Once the parent pays GamePlannr’s $4 booking fee, the session is confirmed. You collect your full training rate directly from the parent in person.',
  },
];

function HomePage() {
  return (
    <div className="home">
      {/* Hero */}
      <section className="home-hero">
        <div className="home-hero-content">
          <h1 className="home-hero-title">
            Built For <span className="home-hero-accent">Young Athletes</span>
          </h1>
          <p className="home-hero-sub">
            Connect with local athletes who make training affordable, relatable, and fun.
          </p>
          <div className="home-hero-ctas">
            <Link to="/mentors" className="btn btn-primary home-cta-btn">
              Find a Trainer
            </Link>
            <Link to="/signup?role=trainer" className="btn btn-outline home-cta-btn">
              Become a Trainer
            </Link>
          </div>
        </div>
      </section>

      <div className="section-divider" aria-hidden="true"></div>

      {/* Sports */}
      <section className="home-sports">
        <div className="page-container">
          <p className="home-section-label">What We Offer</p>
          <h2 className="section-title home-section-center">Sports We Cover</h2>
          <p className="section-subtitle home-section-center">
            Find trainers across all the most popular youth sports.
          </p>
          <div className="home-sports-grid">
            {SPORTS.map((sport) => (
              <Link
                key={sport}
                to={`/mentors?sport=${encodeURIComponent(sport)}`}
                className="home-sport-card"
              >
                <span className="home-sport-icon" role="img" aria-label={sport}>
                  {SPORT_ICONS[sport] || '🏅'}
                </span>
                <span className="home-sport-name">{sport}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <div className="section-divider" aria-hidden="true"></div>

      {/* How it works */}
      <section className="home-how">
        <div className="page-container">
          <div className="home-how-grid">
            {/* For Parents */}
            <div>
              <div className="home-how-header">
                <span className="home-how-tag">For Parents</span>
                <h2 className="section-title">How It Works</h2>
              </div>
              <div className="home-steps">
                {PARENT_STEPS.map((step) => (
                  <div key={step.number} className="home-step">
                    <div className="home-step-number">{step.number}</div>
                    <div className="home-step-body">
                      <h3 className="home-step-title">{step.title}</h3>
                      <p className="home-step-desc">{step.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              <Link to="/mentors" className="btn btn-primary home-how-cta">
                Browse Trainers
              </Link>
            </div>

            <div className="home-how-divider" aria-hidden="true"></div>

            {/* For Mentors */}
            <div>
              <div className="home-how-header">
                <span className="home-how-tag">For Trainers</span>
                <h2 className="section-title">Earn Money Doing What You Love</h2>
              </div>
              <div className="home-steps">
                {MENTOR_STEPS.map((step) => (
                  <div key={step.number} className="home-step">
                    <div className="home-step-number">{step.number}</div>
                    <div className="home-step-body">
                      <h3 className="home-step-title">{step.title}</h3>
                      <p className="home-step-desc">{step.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              <Link to="/signup?role=trainer" className="btn btn-primary home-how-cta">
                Start Training
              </Link>
            </div>
          </div>
        </div>
      </section>

      <div className="section-divider" aria-hidden="true"></div>

      {/* Mission */}
      <section className="home-mission">
        <div className="page-container home-mission-inner">
          <h2 className="home-mission-title">Why GamePlannr?</h2>
          <p className="home-mission-text">
            Professional sports training is expensive and often out of reach for everyday families.
            GamePlannr connects parents with skilled teen athletes in their own neighborhood — trainers
            who are relatable, affordable, and passionate about their sport. We believe every kid
            deserves a great game plan, no matter their budget.
          </p>
          <div className="home-mission-stats">
            <div className="home-stat">
              <span className="home-stat-value">$4</span>
              <span className="home-stat-label">Flat booking fee</span>
            </div>
            <div className="home-stat">
              <span className="home-stat-value">8</span>
              <span className="home-stat-label">Sports covered</span>
            </div>
            <div className="home-stat">
              <span className="home-stat-value">100%</span>
              <span className="home-stat-label">Local trainers</span>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default HomePage;
